import { useState } from 'react';
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Plus, Trash2, Loader2, ChevronDown, ChevronUp, ShieldAlert, Building2, Radar, type LucideIcon } from 'lucide-react';
import { watchlistApi } from '@/api/watchlist';
import { Modal, FieldLabel, TextInput, TextArea, PrimaryButton, GhostButton } from '@/components/watchlist/Modal';
import { ReferencePhotoUpload } from '@/components/watchlist/ReferencePhotoUpload';
import { BlobImage } from '@/components/watchlist/BlobImage';
import { EntityGraphPanel } from '@/components/watchlist/EntityGraphPanel';
import { wl } from '@/components/watchlist/theme';
import type { BrandProfileRecord, BrandDetectionResult } from '@/types/watchlist';

interface HandleRow { platform: string; handle: string }

interface FormState {
  name: string;
  aliases: string;
  domains: string;
  contactNumbers: string;
  handles: HandleRow[];
}

function recordToForm(r?: BrandProfileRecord): FormState {
  return {
    name: r?.name ?? '',
    aliases: r?.aliases.join(', ') ?? '',
    domains: r?.official_domains.join(', ') ?? '',
    contactNumbers: r?.contact_numbers.join(', ') ?? '',
    handles: r ? Object.entries(r.official_handles).map(([platform, handle]) => ({ platform, handle })) : [{ platform: 'twitter', handle: '' }],
  };
}

function CreateEditModal({ initial, onClose, onSaved }: { initial?: BrandProfileRecord; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState<FormState>(recordToForm(initial));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function updateHandle(i: number, patch: Partial<HandleRow>) {
    setForm((f) => ({ ...f, handles: f.handles.map((h, idx) => (idx === i ? { ...h, ...patch } : h)) }));
  }

  async function handleSave() {
    if (!form.name.trim()) { setError('Name is required.'); return; }
    setSaving(true);
    setError(null);
    try {
      const official_handles: Record<string, string> = {};
      for (const h of form.handles) if (h.platform.trim() && h.handle.trim()) official_handles[h.platform.trim()] = h.handle.trim();
      const body = {
        name: form.name.trim(),
        aliases: form.aliases.split(',').map((s) => s.trim()).filter(Boolean),
        official_handles,
        official_domains: form.domains.split(',').map((s) => s.trim()).filter(Boolean),
        contact_numbers: form.contactNumbers.split(',').map((s) => s.trim()).filter(Boolean),
        is_active: true,
      };
      if (initial) await watchlistApi.updateBrandProfile(initial.id, body);
      else await watchlistApi.createBrandProfile(body);
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title={initial ? 'Edit Brand Profile' : 'New Brand Profile'} onClose={onClose} maxWidth={620} accent={wl.green}>
      <div className="flex flex-col gap-4">
        <div>
          <FieldLabel>Brand name</FieldLabel>
          <TextInput value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Acme Corp" />
        </div>
        <div>
          <FieldLabel>Aliases, any script (comma-separated)</FieldLabel>
          <TextInput value={form.aliases} onChange={(e) => setForm((f) => ({ ...f, aliases: e.target.value }))} placeholder="AcmeCo, Acme Bank" />
        </div>
        <div>
          <FieldLabel>Official domains (comma-separated)</FieldLabel>
          <TextInput value={form.domains} onChange={(e) => setForm((f) => ({ ...f, domains: e.target.value }))} placeholder="acme.com, acme.co.in" />
        </div>
        <div>
          <FieldLabel>Helplines / contact numbers (comma-separated)</FieldLabel>
          <TextInput value={form.contactNumbers} onChange={(e) => setForm((f) => ({ ...f, contactNumbers: e.target.value }))} placeholder="+1-800-555-0100" />
        </div>
        <div>
          <FieldLabel>Official handles per platform — anything else using these names is a candidate</FieldLabel>
          <div className="flex flex-col gap-2">
            {form.handles.map((h, i) => (
              <div key={i} className="flex gap-2 items-center">
                <TextInput style={{ width: 120 }} value={h.platform} onChange={(e) => updateHandle(i, { platform: e.target.value })} placeholder="twitter" />
                <TextInput style={{ flex: 1 }} value={h.handle} onChange={(e) => updateHandle(i, { handle: e.target.value })} placeholder="@acme_official" />
                <button onClick={() => setForm((f) => ({ ...f, handles: f.handles.filter((_, idx) => idx !== i) }))} style={{ color: wl.textMuted }}>
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
          <button
            onClick={() => setForm((f) => ({ ...f, handles: [...f.handles, { platform: '', handle: '' }] }))}
            className="flex items-center gap-1.5 mt-2"
            style={{ color: wl.green, fontSize: 12 }}
          >
            <Plus size={13} /> Add platform
          </button>
        </div>

        {error && <p style={{ color: wl.critical, fontSize: 12 }}>{error}</p>}

        <div className="flex justify-end gap-2 mt-2">
          <GhostButton onClick={onClose}>Cancel</GhostButton>
          <PrimaryButton onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</PrimaryButton>
        </div>
      </div>
    </Modal>
  );
}

function ThreatList({ result }: { result: BrandDetectionResult }) {
  if (result.total_threats === 0) return <p style={{ color: wl.greenDim, fontSize: 12.5, marginTop: 10 }}>No threats detected.</p>;
  return (
    <div className="flex flex-col gap-2 mt-3">
      {result.threats.map((t, i) => (
        <div key={i} className="flex items-start gap-2 py-2 px-3" style={{ background: wl.criticalWash, border: `1px solid ${wl.critical}28`, borderRadius: 8 }}>
          <ShieldAlert size={14} style={{ color: wl.critical, marginTop: 1, flexShrink: 0 }} />
          <div>
            <p style={{ color: wl.text, fontSize: 12.5 }}>{t.indicators}</p>
            <p style={{ color: wl.textMuted, fontSize: 10.5, marginTop: 2 }}>
              {t.threat_type} · severity {t.severity} · confidence {(t.confidence * 100).toFixed(0)}%
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

function DetectionPanel({ profile }: { profile: BrandProfileRecord }) {
  const [content, setContent] = useState('');
  const [image, setImage] = useState<File | null>(null);
  const [result, setResult] = useState<BrandDetectionResult | null>(null);
  const [running, setRunning] = useState(false);

  const statsQuery = useQuery({
    queryKey: ['watchlist-brand-stats', profile.id],
    queryFn: () => watchlistApi.getBrandProtectionStatistics({ brand_profile_id: profile.id }),
  });

  async function runDetect() {
    setRunning(true);
    try {
      const res = image
        ? await watchlistApi.detectBrandImpersonationWithImage({ image, content, source_platform: 'twitter', brand_profile_id: profile.id })
        : await watchlistApi.detectBrandImpersonation({ content, source_platform: 'twitter', brand_profile_id: profile.id });
      setResult(res);
      statsQuery.refetch();
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <p style={{ color: wl.textMuted, fontSize: 11, letterSpacing: '0.06em', fontWeight: 700, marginBottom: 8 }}>TEST DETECTION</p>
        <TextArea value={content} onChange={(e) => setContent(e.target.value)} placeholder="Paste suspicious text to check against this brand's impersonation/phishing patterns…" />
        <div className="flex items-center gap-3 mt-2">
          <input type="file" accept="image/*" onChange={(e) => setImage(e.target.files?.[0] ?? null)} style={{ color: wl.textMuted, fontSize: 11.5 }} />
          <PrimaryButton onClick={runDetect} disabled={running || (!content.trim() && !image)}>{running ? 'Checking…' : 'Run detection'}</PrimaryButton>
        </div>
        {result && <ThreatList result={result} />}
      </div>
      <div>
        <p style={{ color: wl.textMuted, fontSize: 11, letterSpacing: '0.06em', fontWeight: 700, marginBottom: 8 }}>STATISTICS (30 DAYS)</p>
        {statsQuery.isLoading ? <Loader2 className="animate-spin" size={16} style={{ color: wl.green }} /> : statsQuery.data && (
          <div className="grid grid-cols-2 gap-2">
            <div style={{ background: wl.bg, border: `1px solid ${wl.border}`, borderRadius: 8, padding: '10px 12px' }}>
              <div style={{ color: wl.text, fontWeight: 800, fontSize: 18 }}>{statsQuery.data.total_threats}</div>
              <div style={{ color: wl.textMuted, fontSize: 10.5 }}>Total threats</div>
            </div>
            <div style={{ background: wl.bg, border: `1px solid ${wl.border}`, borderRadius: 8, padding: '10px 12px' }}>
              <div style={{ color: wl.critical, fontWeight: 800, fontSize: 18 }}>{statsQuery.data.by_severity.critical + statsQuery.data.by_severity.high}</div>
              <div style={{ color: wl.textMuted, fontSize: 10.5 }}>High/critical</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function RecentThreatsPanel({ profileId, profileLogoAssetIds }: { profileId: string; profileLogoAssetIds: string[] }) {
  const query = useQuery({
    queryKey: ['watchlist-brand-threats', profileId],
    queryFn: () => watchlistApi.listBrandProtectionThreats(profileId),
  });

  return (
    <div>
      <p style={{ color: wl.textMuted, fontSize: 11, letterSpacing: '0.06em', fontWeight: 700, marginBottom: 8 }}>
        ALL DETECTED THREATS (manual tests + background scan, every 60 min)
      </p>
      {query.isLoading ? (
        <Loader2 className="animate-spin" size={16} style={{ color: wl.green }} />
      ) : (query.data?.threats.length ?? 0) === 0 ? (
        <p style={{ color: wl.textFaint, fontSize: 12 }}>No threats detected yet.</p>
      ) : (
        <div className="flex flex-col gap-2">
          {query.data!.threats.map((t, i) => {
            const meta = (t.metadata ?? {}) as Record<string, unknown>;
            const isLogoMatch = meta.pattern_name === 'logo_reuse';
            const sourceUrl = typeof meta.source_url === 'string' ? meta.source_url : undefined;
            const snippet = typeof meta.matched_content_snippet === 'string' ? meta.matched_content_snippet : undefined;
            const matchScore = typeof meta.match_score === 'number' ? meta.match_score : undefined;
            const matchedAssetId = typeof meta.matched_asset_id === 'string' ? meta.matched_asset_id : undefined;
            const candidateImageUrl = typeof meta.candidate_image_url === 'string' ? meta.candidate_image_url : undefined;
            return (
              <div key={i} className="flex flex-col gap-2 py-2.5 px-3" style={{ background: wl.bg, border: `1px solid ${wl.border}`, borderRadius: 8 }}>
                <div className="flex items-start gap-2">
                  <ShieldAlert size={14} style={{ color: wl.critical, marginTop: 1, flexShrink: 0 }} />
                  <div className="min-w-0 flex-1">
                    <p style={{ color: wl.text, fontSize: 12.5 }}>{t.indicators}</p>
                    <p style={{ color: wl.textMuted, fontSize: 10.5, marginTop: 2 }}>
                      {t.source_platform} · {new Date(t.timestamp).toLocaleString()} · severity {t.severity}
                      {matchScore != null && <> · match {(matchScore * 100).toFixed(0)}%</>}
                    </p>
                    {snippet && <p style={{ color: wl.textMuted, fontSize: 11.5, marginTop: 4, lineHeight: 1.5 }}>"{snippet}"</p>}
                  </div>
                  {sourceUrl && (
                    <a href={sourceUrl} target="_blank" rel="noreferrer" style={{ color: wl.green, fontSize: 11, flexShrink: 0, whiteSpace: 'nowrap' }}>
                      View post →
                    </a>
                  )}
                </div>
                {isLogoMatch && matchedAssetId && profileLogoAssetIds.includes(matchedAssetId) && (
                  <div className="flex items-center gap-3 mt-1">
                    <div className="flex flex-col items-center gap-1">
                      <BlobImage
                        path={`/watchlist/brand-profiles/${profileId}/reference-logos/${matchedAssetId}/image`}
                        style={{ width: 64, height: 64, borderRadius: 8, objectFit: 'cover', border: `1px solid ${wl.border}` }}
                      />
                      <span style={{ color: wl.textMuted, fontSize: 9.5 }}>YOUR LOGO</span>
                    </div>
                    <span style={{ color: wl.textMuted, fontSize: 11 }}>vs</span>
                    {candidateImageUrl && (
                      <div className="flex flex-col items-center gap-1">
                        <BlobImage
                          path={`/watchlist/proxy-image?url=${encodeURIComponent(candidateImageUrl)}`}
                          alt="candidate"
                          style={{ width: 64, height: 64, borderRadius: 8, objectFit: 'cover', border: `1px solid ${wl.border}` }}
                        />
                        <span style={{ color: wl.textMuted, fontSize: 9.5 }}>FOUND ON {t.source_platform.toUpperCase()}</span>
                      </div>
                    )}
                    {matchScore != null && (
                      <span
                        className="px-2.5 py-1 rounded-full font-bold"
                        style={{ background: matchScore >= 0.9 ? wl.criticalWash : wl.mediumWash, color: matchScore >= 0.9 ? wl.critical : wl.medium, fontSize: 13 }}
                      >
                        {(matchScore * 100).toFixed(0)}% match
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }: { icon: LucideIcon; label: string; value: number | string; color: string }) {
  return (
    <div style={{ background: wl.surface, border: `1px solid ${wl.border}`, borderRadius: 10, padding: '18px 20px' }} className="flex items-center gap-4">
      <div style={{ width: 46, height: 46, borderRadius: 8, background: `${color}18`, border: `1px solid ${color}30` }} className="flex items-center justify-center flex-shrink-0">
        <Icon size={21} style={{ color }} />
      </div>
      <div className="min-w-0">
        <div style={{ color: wl.text, fontWeight: 800, fontSize: 26, lineHeight: 1.1 }}>{value}</div>
        <div style={{ color: wl.textMuted, fontSize: 11, marginTop: 3, letterSpacing: '0.04em', fontWeight: 600 }}>{label}</div>
      </div>
    </div>
  );
}

const TIME_RANGES = [7, 30, 90];

export function BrandsPanel() {
  const qc = useQueryClient();
  const [modalFor, setModalFor] = useState<'new' | BrandProfileRecord | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [timePeriod, setTimePeriod] = useState(30);

  const query = useQuery({ queryKey: ['watchlist-brand-profiles'], queryFn: () => watchlistApi.listBrandProfiles() });
  const profiles = query.data?.brand_profiles ?? [];

  const statsQueries = useQueries({
    queries: profiles.map((p) => ({
      queryKey: ['watchlist-brand-stats-summary', p.id, timePeriod],
      queryFn: () => watchlistApi.getBrandProtectionStatistics({ brand_profile_id: p.id, time_period: timePeriod }),
      enabled: profiles.length > 0,
    })),
  });
  const statsByProfileId = new Map(profiles.map((p, i) => [p.id, statsQueries[i]?.data]));
  const totalDetections = statsQueries.reduce((sum, q) => sum + (q.data?.total_threats ?? 0), 0);
  const statsLoading = statsQueries.some((q) => q.isLoading);

  const deleteMut = useMutation({
    mutationFn: (id: string) => watchlistApi.deleteBrandProfile(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['watchlist-brand-profiles'] }),
  });

  function refetch() {
    qc.invalidateQueries({ queryKey: ['watchlist-brand-profiles'] });
  }

  return (
    <div>
      {modalFor && (
        <CreateEditModal initial={modalFor === 'new' ? undefined : modalFor} onClose={() => setModalFor(null)} onSaved={refetch} />
      )}

      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 style={{ color: wl.text, fontWeight: 800, fontSize: 15 }}>Brand profiles</h2>
          <p style={{ color: wl.textMuted, fontSize: 12, marginTop: 2 }}>Impersonation/phishing text rules + CLIP logo-reuse matching</p>
        </div>
        <PrimaryButton onClick={() => setModalFor('new')}>+ New brand profile</PrimaryButton>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <StatCard icon={Building2} label="BRAND PROFILES" value={profiles.length} color={wl.medium} />
        <StatCard icon={Radar} label={`DETECTIONS (${timePeriod}D)`} value={statsLoading ? '—' : totalDetections} color={wl.critical} />
        <div style={{ background: wl.surface, border: `1px solid ${wl.border}`, borderRadius: 10, padding: '18px 20px' }} className="flex items-center justify-between">
          <span style={{ color: wl.textMuted, fontSize: 11, letterSpacing: '0.04em', fontWeight: 600 }}>TIME RANGE</span>
          <select
            value={timePeriod}
            onChange={(e) => setTimePeriod(Number(e.target.value))}
            style={{ background: wl.bg, border: `1px solid ${wl.border}`, color: wl.text, fontSize: 13, borderRadius: 6, padding: '6px 10px', fontFamily: wl.font }}
          >
            {TIME_RANGES.map((d) => <option key={d} value={d}>Last {d} days</option>)}
          </select>
        </div>
      </div>

      {query.isLoading ? (
        <Loader2 className="animate-spin" size={22} style={{ color: wl.green }} />
      ) : profiles.length === 0 ? (
        <p style={{ color: wl.textFaint, fontSize: 13 }}>No brand profiles yet.</p>
      ) : (
        <div className="flex flex-col gap-3">
          {profiles.map((profile) => {
            const stats = statsByProfileId.get(profile.id);
            const detections = stats?.total_threats ?? 0;
            const critHigh = (stats?.by_severity.critical ?? 0) + (stats?.by_severity.high ?? 0);
            return (
            <motion.div key={profile.id} layout style={{ background: wl.surface, border: `1px solid ${wl.border}`, borderRadius: 10 }}>
              <div className="flex items-center justify-between px-5 py-4 gap-4">
                <div className="min-w-0 flex-1 cursor-pointer" onClick={() => setExpanded(expanded === profile.id ? null : profile.id)}>
                  <span style={{ color: wl.text, fontWeight: 700, fontSize: 14 }}>{profile.name}</span>
                  <p style={{ color: wl.textMuted, fontSize: 11.5, marginTop: 3 }}>
                    {Object.keys(profile.official_handles).length} handle(s) · {profile.official_domains.length} domain(s) ·{' '}
                    {profile.reference_logo_asset_ids.length} logo(s)
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span
                    className="inline-flex items-center px-3 py-1.5 rounded-full font-bold tracking-wide"
                    style={{ background: detections > 0 ? wl.criticalWash : wl.greenWash, color: detections > 0 ? wl.critical : wl.greenDim, fontSize: 12 }}
                    title={`${critHigh} high/critical`}
                  >
                    {detections} detection{detections === 1 ? '' : 's'}
                  </span>
                  <GhostButton onClick={() => setModalFor(profile)}>Edit</GhostButton>
                  <GhostButton destructive onClick={() => confirm(`Delete "${profile.name}"?`) && deleteMut.mutate(profile.id)}>
                    <Trash2 size={13} />
                  </GhostButton>
                  <button onClick={() => setExpanded(expanded === profile.id ? null : profile.id)} style={{ color: wl.textMuted }}>
                    {expanded === profile.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                </div>
              </div>
              {expanded === profile.id && (
                <div className="px-5 pb-5 flex flex-col gap-5" style={{ borderTop: `1px solid ${wl.border}`, paddingTop: 16 }}>
                  <ReferencePhotoUpload
                    label="Reference logos"
                    assetIds={profile.reference_logo_asset_ids}
                    onUpload={async (file) => {
                      const updated = await watchlistApi.uploadBrandLogo(profile.id, file);
                      refetch();
                      return updated.reference_logo_asset_ids[updated.reference_logo_asset_ids.length - 1];
                    }}
                    onDelete={async (assetId) => {
                      await watchlistApi.deleteBrandLogo(profile.id, assetId);
                      refetch();
                    }}
                    getImagePath={(assetId) => `/watchlist/brand-profiles/${profile.id}/reference-logos/${assetId}/image`}
                  />
                  {profile.entity_id && <EntityGraphPanel entityId={profile.entity_id} />}
                  <DetectionPanel profile={profile} />
                  <RecentThreatsPanel profileId={profile.id} profileLogoAssetIds={profile.reference_logo_asset_ids} />
                </div>
              )}
            </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
